import { Link, Navigate, NavLink, useParams } from 'react-router-dom';
import { AnimeGrid } from '@/components/anime/AnimeGrid';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { fromLibraryEntry } from '@/api/mappers';
import { LIBRARY_LABELS } from '@/lib/constants';
import { useLibrary } from '@/hooks/useLibrary';
import type { LibraryKey } from '@/types/anime';

const TABS: LibraryKey[] = ['favorites', 'watchlist', 'history'];

const EMPTY_COPY: Record<LibraryKey, { title: string; description: string }> = {
	favorites: {
		title: 'Chưa có anime yêu thích',
		description: 'Bấm ♡ trên poster bất kỳ để lưu vào đây.',
	},
	watchlist: {
		title: 'Danh sách xem đang trống',
		description: 'Thêm phim bằng nút ＋ Danh sách ở trang chi tiết.',
	},
	history: {
		title: 'Chưa có lịch sử xem',
		description: 'Những phim bạn mở sẽ xuất hiện ở đây để xem tiếp.',
	},
};

export function LibraryPage() {
	const { tab } = useParams<{ tab?: string }>();
	const library = useLibrary();

	if (!tab || !TABS.includes(tab as LibraryKey)) {
		return <Navigate to="/thu-vien/favorites" replace />;
	}

	const activeKey = tab as LibraryKey;
	const entries = library[activeKey];
	const items = entries.map(fromLibraryEntry);

	return (
		<section className="mt-6">
			<SectionHeader
				title={LIBRARY_LABELS[activeKey]}
				action={
					entries.length > 0 && (
						<button
							type="button"
							onClick={() => library.clear(activeKey)}
							className="text-[13px] font-bold text-muted transition hover:text-accent"
						>
							Xoá tất cả
						</button>
					)
				}
			/>

			<div className="mb-5 flex flex-wrap gap-2">
				{TABS.map((key) => (
					<NavLink
						key={key}
						to={`/thu-vien/${key}`}
						className={({ isActive }) =>
							`rounded-full border px-3.5 py-2 text-sm transition ${
								isActive
									? 'border-accent bg-accent/15 text-ink'
									: 'border-line bg-white/[0.02] text-muted hover:border-accent hover:text-ink'
							}`
						}
					>
						{LIBRARY_LABELS[key]}
						<span className="ml-1.5 opacity-70">{library.counts[key]}</span>
					</NavLink>
				))}
			</div>

			<AnimeGrid
				items={items}
				emptyTitle={EMPTY_COPY[activeKey].title}
				emptyDescription={EMPTY_COPY[activeKey].description}
				emptyAction={
					<Link
						to="/duyet"
						className="rounded-lg bg-accent px-4 py-2 text-sm font-bold text-white"
					>
						Duyệt kho anime
					</Link>
				}
				onRemove={(anime) => library.remove(activeKey, anime.slug)}
			/>
		</section>
	);
}
